import { apiClient } from '@/plugins/axios/api'
import type { TypeOuvrageMarkerConfig } from '@/models/interfaces/map/MarkerConfig'

const RECALCULATE_TIMEOUT_MS = 300000

export interface RecalculateIndicatorsPayload {
  date: string
  atgId?: number | null
}

export interface TypeOuvrageAdmin {
  /** Primary key for PUT/DELETE. API may return id or typId. */
  id?: number
  typeOuvrageCode: string
  typeOuvrageLib: string
  /** Libellé anglais (type_ouvrage_lib_en) ; null tant qu'il n'est pas saisi. */
  typeOuvrageLibEn?: string | null
  competenceId: number
  /** API may return snake_case */
  type_ouvrage_code?: string
  type_ouvrage_lib?: string
  type_ouvrage_lib_en?: string | null
  competence_id?: number
  typId?: number
  /** Domain for Martin layer mapping: view_infrastructures_domaine_<domaineCode> */
  domaineCode?: string
  domaineLib?: string
  domaine_id?: number
  domaine_code?: string
  domaine_lib?: string
  /** Number of attributes linked to this type */
  attributCount?: number
  attribut_count?: number
  /** Marker/icon config per type (from API marker_config). Used when typeProperty === "type_ouvrage". */
  markerConfig?: TypeOuvrageMarkerConfig | null
  marker_config?: TypeOuvrageMarkerConfig | null
}

export interface UpdateTypeOuvragePayload {
  typeOuvrageLib?: string
  /** Chaîne vide acceptée : elle efface la traduction, le champ omis la laisse inchangée. */
  typeOuvrageLibEn?: string
  competenceId?: number
  markerConfig?: TypeOuvrageMarkerConfig | null
}

export interface CreateTypeOuvragePayload {
  typeOuvrageCode: string
  typeOuvrageLib: string
  /** Facultatif : un type peut n'être saisi qu'en français. */
  typeOuvrageLibEn?: string
  competenceId: number
}

export interface CreateInfrastructureAttributePayload {
  competenceId: number
  typeOuvrageCode?: string
  attribut: {
    atgRefCode: string
    atgTitre: string
    atgNature: string
    atgDescription?: string
  }
  attributChoix?: Array<{ agcRefCode: string; agcValeur: string; agcOrdre?: number }>
}

/** Result of recalculateIndicators: backend may return 202 Accepted (not yet in DB) or 200 OK (completed). */
export interface RecalculateIndicatorsResult {
  status: 'accepted' | 'completed'
}

/** Status values from GET /api/admin/indicators/recalculate/status. */
export type RecalculateStatusValue = 'pending' | 'running' | 'completed' | 'failed' | 'unknown'

/** Response of GET /api/admin/indicators/recalculate/status?atg_id={atgId}. */
export interface RecalculateStatusResponse {
  atgId: number
  status: RecalculateStatusValue
  message: string | null
  startedAt: string | null
  /** Progress 0-100 when available; null at end or when not provided. */
  progress?: number | null
}

/**
 * GET /api/admin/indicators/recalculate/status?atg_id={atgId}
 * Poll this after POST /api/indicator_sets (201) until status is completed, failed, or unknown.
 * Requires ROLE_SUPER_ADMIN.
 */
export async function getRecalculateStatus(atgId: number): Promise<RecalculateStatusResponse> {
  const response = await apiClient.get<RecalculateStatusResponse>(
    '/api/admin/indicators/recalculate/status',
    { params: { atg_id: atgId } },
  )
  return response.data
}

/**
 * POST /api/admin/indicators/recalculate
 * Body: { date: "YYYY-MM-DD", atgId?: number | null }
 * If atgId is null or omitted, full recalculation. Requires ROLE_SUPER_ADMIN.
 * Returns status: 'accepted' when recalc is queued (202), 'completed' when done (200).
 */
export async function recalculateIndicators(
  payload: RecalculateIndicatorsPayload,
): Promise<RecalculateIndicatorsResult> {
  const response = await apiClient.post<{ status?: string }>(
    '/api/admin/indicators/recalculate',
    payload,
    {
      headers: { 'Content-Type': 'application/json' },
      timeout: RECALCULATE_TIMEOUT_MS,
    },
  )
  const httpStatus = response.status
  const bodyStatus = response.data?.status
  if (httpStatus === 202 || bodyStatus === 'accepted') {
    return { status: 'accepted' }
  }
  return { status: 'completed' }
}

interface HydraCollection<T> {
  'hydra:member'?: T[]
}

/**
 * GET /api/admin/type_ouvrages
 * List types d'ouvrage. Requires ROLE_SUPER_ADMIN.
 */
export async function getTypeOuvrages(): Promise<TypeOuvrageAdmin[]> {
  const response = await apiClient.get<HydraCollection<TypeOuvrageAdmin>>('/api/admin/type_ouvrages')
  const member = response.data['hydra:member'] ?? response.data ?? []
  return Array.isArray(member) ? member : []
}

/**
 * POST /api/admin/type_ouvrages
 * Create an infrastructure (type d'ouvrage). Requires ROLE_SUPER_ADMIN.
 */
export async function createTypeOuvrage(payload: CreateTypeOuvragePayload): Promise<TypeOuvrageAdmin> {
  const response = await apiClient.post<TypeOuvrageAdmin>('/api/admin/type_ouvrages', payload, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}

/**
 * PUT /api/admin/type_ouvrages/{id}
 * Update an infrastructure. Requires ROLE_SUPER_ADMIN.
 * Sends markerConfig in body when provided; backend persists marker_config.
 */
export async function updateTypeOuvrage(
  id: number,
  payload: UpdateTypeOuvragePayload,
): Promise<TypeOuvrageAdmin> {
  const response = await apiClient.put<TypeOuvrageAdmin>(
    `/api/admin/type_ouvrages/${encodeURIComponent(String(id))}`,
    payload,
    { headers: { 'Content-Type': 'application/json' } },
  )
  return response.data
}

/**
 * DELETE /api/admin/type_ouvrages/{id}
 * Delete an infrastructure. Backend must clean references (e.g. refresh indicators). Requires ROLE_SUPER_ADMIN.
 */
export async function deleteTypeOuvrage(id: number): Promise<void> {
  await apiClient.delete(`/api/admin/type_ouvrages/${encodeURIComponent(String(id))}`)
}

export interface MediaObjectItem {
  '@id'?: string
  id?: number
  contentUrl: string
  contentsFilteredUrl?: { thumbnail?: string }
  originalName?: string
  mimeType?: string
}

/**
 * GET /api/media_objects
 * Returns the list of uploaded media objects (images, SVGs, etc.) available in the library.
 */
export async function getMediaObjects(page: number = 1): Promise<MediaObjectItem[]> {
  const response = await apiClient.get<HydraCollection<MediaObjectItem>>('/api/media_objects', {
    params: { page, itemsPerPage: 60 },
  })
  const member = response.data['hydra:member'] ?? (Array.isArray(response.data) ? response.data : [])
  return Array.isArray(member) ? member : []
}

/**
 * POST /api/admin/infrastructure_attributes
 * Create attribute and link to competence (and optionally type d'ouvrage). Requires ROLE_SUPER_ADMIN.
 */
export async function createInfrastructureAttribute(
  payload: CreateInfrastructureAttributePayload,
): Promise<unknown> {
  const response = await apiClient.post('/api/admin/infrastructure_attributes', payload, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data
}
